import { Table, Text, Badge, Paper, Title, Box, ActionIcon, Button, Group, List } from '@mantine/core'
import { IconDoorExit, IconTrash } from '@tabler/icons-react'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react'
import CurrentUser from '../graphql/queries/CurrentUser'
import LeaveRoom from '../graphql/queries/LeaveRoom'
import DeleteRoom from '../graphql/queries/DeleteRoom'
import PublicRooms from '../graphql/queries/PublicRooms'
import SlotsAvailable from '../graphql/queries/SlotsAvailable'
import useCurrentUser from '../hooks/useCurrentUser'
import { useEffect, useState } from 'react'

export default function MyRooms({ onNavigate }) {
    const { data } = useQuery(CurrentUser)
    const rooms = data?.user?.rooms
    const currentUser = useCurrentUser()
    const dispatch = useAppDispatch()
    const [canCreate, setCanCreate] = useState(false)

    const [getSlotsAvailable, { data: slotsData }] = useLazyQuery(SlotsAvailable)

    const [leaveRoomMutation] = useMutation(LeaveRoom, {
        refetchQueries: [{ query: CurrentUser }]
    })

    const [deleteRoomMutation] = useMutation(DeleteRoom, {
        refetchQueries: [
            { query: CurrentUser },
            { query: PublicRooms }
        ]
    })

    useEffect(() => {
        if (!currentUser || !data?.user) {
            setCanCreate(false)
            return
        }

        const userRoles = data.user.roles || []

        // Check if user has admin role (unlimited rooms)
        if (userRoles.includes('admin')) {
            setCanCreate(true)
            return
        }

        // Check if user is not a guest
        if (!userRoles.includes('guest')) {
            getSlotsAvailable()
        } else {
            setCanCreate(false)
        }
    }, [currentUser, data?.user, getSlotsAvailable])

    useEffect(() => {
        if (slotsData?.user?.roomSlotsAvailable !== undefined) {
            setCanCreate(slotsData.user.roomSlotsAvailable > 0)
        }
    }, [slotsData])

    function navigateToRoom(room) {
        dispatch(Actions.setCurrentRoom(room.id))
        dispatch(Actions.navigateTo('listen'))
        onNavigate?.()
    }

    function handleLeaveRoom(room, event) {
        event.stopPropagation() // Prevent row click navigation

        const isPublic = room.public
        const message = isPublic
            ? `Are you sure you want to leave ${room.name}? You will be removed from the room but can rejoin later since it's public.`
            : `Are you sure you want to leave ${room.name}? You will be permanently removed and won't be able to return without a new invitation.`

        dispatch(Actions.showConfirmModal(
            message,
            'Leave Room',
            async () => {
                await leaveRoomMutation({ variables: { roomId: room.id } })
            }
        ))
    }

    function handleDeleteRoom(room, event) {
        event.stopPropagation() // Prevent row click navigation

        const message = (
            <>
                <Text fw={600} c="red" mb="sm">
                    ⚠️ WARNING: You are about to permanently delete "{room.name}". This action cannot be undone.
                </Text>
                <Text mb="xs">
                    All room content will be permanently lost, including:
                </Text>
                <List size="sm" spacing="xs" mb="md">
                    <List.Item>All chat messages and history</List.Item>
                    <List.Item>The music queue and track selections</List.Item>
                    <List.Item>All member access and room settings</List.Item>
                    <List.Item>Any shared content and metadata</List.Item>
                </List>
                <Text fw={500}>
                    Are you absolutely sure you want to delete this room?
                </Text>
            </>
        )

        dispatch(Actions.showConfirmModal(
            message,
            'Delete Room Permanently',
            async () => {
                await deleteRoomMutation({ variables: { roomId: room.id } })
            }
        ))
    }

    function userIsOwner(room) {
        return room.owner.id === currentUser?.id
    }

    if (!rooms || rooms.length === 0) {
        return (
            <Box>
                <Group justify="space-between" mb="xs">
                    <Title order={5} c="gray.3" style={{ textAlign: 'left' }}>My Rooms</Title>
                    {canCreate && (
                        <Button
                            variant="filled"
                            color="blue"
                            onClick={() => dispatch(Actions.showCreateRoomModal())}
                        >
                            Create Room
                        </Button>
                    )}
                </Group>
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                        You aren't a member of any rooms.
                    </Text>
                </Paper>
            </Box>
        )
    }

    return (
        <Box>
            <Group justify="space-between" mb="xs">
                <Title order={5} c="gray.3" style={{ textAlign: 'left' }}>My Rooms</Title>
                {canCreate && (
                    <Button
                        variant="filled"
                        color="blue"
                        onClick={() => dispatch(Actions.showCreateRoomModal())}
                    >
                        Create Room
                    </Button>
                )}
            </Group>
            <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Table highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ textAlign: 'left' }}>Name</Table.Th>
                            <Table.Th style={{ textAlign: 'left' }}>Topic</Table.Th>
                            <Table.Th style={{ textAlign: 'center' }}>Members</Table.Th>
                            <Table.Th style={{ textAlign: 'center', width: '80px' }}>Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rooms.map((room) => (
                            <Table.Tr
                                key={room.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigateToRoom(room)}
                            >
                                <Table.Td style={{ textAlign: 'left' }}>
                                    <Text c="indigo.4" fw={500} style={{ cursor: 'pointer' }}>
                                        {room.name}
                                    </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'left' }}>
                                    <Text size="sm" c="dimmed">{room.topic}</Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'center' }}>
                                    <Badge size="sm" variant="light" color="gray">
                                        {room.members.length}
                                    </Badge>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'center' }}>
                                    {userIsOwner(room) ? (
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            size="sm"
                                            onClick={(e) => handleDeleteRoom(room, e)}
                                            title="Delete room"
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    ) : (
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            size="sm"
                                            onClick={(e) => handleLeaveRoom(room, e)}
                                            title="Leave room"
                                        >
                                            <IconDoorExit size={16} />
                                        </ActionIcon>
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Paper>
        </Box>
    )
}

